import { useState } from "react";
import { DIRECTIONS, PhotoDraft, PhotoSection, SECTIONS } from "./photoModel";
import { ExistingPhoto } from "./conflicts";

export interface ProductOption {
  productKey: string;
  barcode: string;
  matCode: string;
  label: string;
}
export interface PhotoEditPopoverProps {
  draft: PhotoDraft;
  fileName?: string;
  products: ProductOption[];
  existing?: ExistingPhoto[];
  onChange(id: string, patch: Partial<PhotoDraft>): void;
  onSetMain(id: string): void;
  onMove(id: string, barcode: string, matCode: string): void;
  onClose(): void;
}

export default function PhotoEditPopover({ draft, fileName, products, existing, onChange, onSetMain, onMove, onClose }: PhotoEditPopoverProps) {
  const { id } = draft;
  // Local, editable target so an image can move to ANY barcode/matcode — not only
  // products already in the batch. Seeded from the draft (parent keys by draft.id,
  // so this resets when a different image is opened).
  const [moveBarcode, setMoveBarcode] = useState(draft.barcode);
  const [moveMatCode, setMoveMatCode] = useState(draft.matCode);
  const moveChanged =
    moveBarcode.trim() !== "" &&
    moveMatCode.trim() !== "" &&
    (moveBarcode.trim() !== draft.barcode || moveMatCode.trim() !== draft.matCode);
  return (
    /* 22rem is measured, not chosen. Below it the "ย้ายไป barcode" field cannot show a
       whole 13-digit barcode (it needs 127px of content box and only gets 129px here), and
       above it the panel starts eating the tree rows' names rather than only the chip
       cluster at their right edge. `max-w` keeps it inside a phone screen. */
    <div className="max-h-[calc(100vh-7rem)] w-[22rem] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-gray-800">แก้รูป</span>
        <button
          type="button"
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          aria-label="ปิด"
        >
          ✕
        </button>
      </div>

      {/* Preview */}
      <img
        src={draft.previewUrl}
        alt={fileName ?? id}
        className="object-contain w-full mb-3 rounded-xl h-32 bg-gray-50 border border-gray-100"
      />

      {/* Section picker */}
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Section</p>
      <div className="flex gap-1.5 mb-3">
        {SECTIONS.map((s: PhotoSection) => (
          <button
            key={s}
            type="button"
            onClick={() => onChange(id, { section: s })}
            className={`flex-1 rounded-lg border py-1.5 text-xs font-medium transition-colors ${
              draft.section === s
                ? "border-blue-500 bg-blue-50 text-blue-700"
                : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Direction picker */}
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">มุม (Direction)</p>
      <div className="flex flex-wrap gap-1 mb-3">
        {DIRECTIONS.map((d) => (
          <button
            key={d.code}
            type="button"
            aria-label={d.label}
            title={`${d.code} → ${d.angle} (${d.label})`}
            onClick={() => onChange(id, { direction: d.code })}
            className={`rounded-lg border px-2 py-1 text-xs font-medium transition-colors ${
              draft.direction === d.code
                ? "border-blue-500 bg-blue-50 text-blue-700"
                : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            {d.code}
          </button>
        ))}
      </div>

      {/* Move to product — free barcode/matcode entry so an image can move to ANY
          product, not only those already in the batch. Batch products offered as
          autocomplete suggestions. */}
      <p className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        ย้ายไป barcode / matcode
      </p>
      <datalist id={`barcodes-${id}`}>
        {Array.from(new Set(products.map((p) => p.barcode))).map((bc) => (
          <option key={bc} value={bc} />
        ))}
      </datalist>
      <datalist id={`matcodes-${id}`}>
        {Array.from(new Set(products.map((p) => p.matCode))).map((mc) => (
          <option key={mc} value={mc} />
        ))}
      </datalist>
      <div className="flex gap-1.5 mb-3">
        <input
          aria-label="ย้ายไป barcode"
          list={`barcodes-${id}`}
          value={moveBarcode}
          onChange={(e) => setMoveBarcode(e.target.value)}
          placeholder="barcode"
          className="min-w-0 flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-300 transition-colors"
        />
        <input
          aria-label="ย้ายไป matcode"
          list={`matcodes-${id}`}
          value={moveMatCode}
          onChange={(e) => setMoveMatCode(e.target.value)}
          placeholder="matcode"
          className="min-w-0 flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-300 transition-colors"
        />
        <button
          type="button"
          aria-label="ย้ายสินค้า"
          disabled={!moveChanged}
          onClick={() => onMove(id, moveBarcode.trim(), moveMatCode.trim())}
          className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ย้าย
        </button>
      </div>

      {/* Set main button */}
      <button
        type="button"
        onClick={() => onSetMain(id)}
        className={`mb-3 w-full rounded-lg border py-1.5 text-xs font-medium transition-colors ${
          draft.isMain
            ? "border-amber-400 bg-amber-50 text-amber-700"
            : "border-gray-200 text-gray-500 hover:border-amber-200 hover:bg-amber-50 hover:text-amber-600"
        }`}
      >
        ★ ตั้งเป็นรูปหลัก
      </button>

      {/* Derived filename */}
      <p className="rounded-lg bg-emerald-50 border border-emerald-100 p-2 text-[11px] text-emerald-700 break-all leading-relaxed">
        {fileName ?? "— (ยังไม่รู้มุม)"}
      </p>

      {/* Existing photos in system */}
      {existing && existing.length > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-orange-600">
            รูปเดิมในระบบ ({existing.length})
          </p>
          {/* Four across, not three: the extra panel width goes into seeing more of the
              existing photos at once, not into taller thumbnails that push the rest of
              the panel off the screen. */}
          <div className="grid grid-cols-4 gap-1">
            {existing.map((e) => (
              <img
                key={e.relPath}
                src={e.url}
                alt={e.name}
                className="object-cover w-full rounded-lg aspect-square border border-orange-100"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
